// Wishlist store: product handles in localStorage under the live theme's key ("m-wishlist-products"), so saved
// pieces carry over. The format is the live theme's (its assets/wishlist.js): a JSON array of product handles, a new
// one appended at the end (oldest first), no duplicates. Bad data is tolerated (anything but an array reads as empty,
// entries that aren't text are skipped) and the key is never deleted. Updates every [data-wishlist-count], and runs
// every heart: <button data-wishlist-toggle data-handle data-label-add data-label-remove aria-pressed>.
// Every change emits "wishlist:changed" { handles, count, handle, added, removed } (the wishlist page follows it).
const DE = (window.DE = window.DE || {});
const KEY = DE.config?.wishlist?.key || 'm-wishlist-products';

const read = () => {
  try {
    const list = JSON.parse(localStorage.getItem(KEY) || '[]');
    return Array.isArray(list) ? [...new Set(list.filter((h) => typeof h === 'string' && h))] : [];
  } catch {
    return [];
  }
};

const write = (list) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    // Storage full or blocked: the change lasts for this page only.
  }
};

let handles = read();

const renderCounts = (scope = document) => {
  scope.querySelectorAll('[data-wishlist-count]').forEach((el) => {
    el.textContent = String(handles.length);
  });
};

// Hearts show the saved state (aria-pressed; the CSS fills the icon) and a matching label.
const renderButtons = (scope = document) => {
  scope.querySelectorAll('[data-wishlist-toggle][data-handle]').forEach((button) => {
    const saved = handles.includes(button.dataset.handle);
    button.setAttribute('aria-pressed', String(saved));
    const label = saved ? button.dataset.labelRemove : button.dataset.labelAdd;
    if (label) button.setAttribute('aria-label', label);
  });
};

const changed = (handle, added, removed = []) => {
  renderCounts();
  renderButtons();
  DE.emit?.('wishlist:changed', { handles: [...handles], count: handles.length, handle, added, removed });
};

DE.wishlist = {
  handles: () => [...handles],
  has: (handle) => handles.includes(handle),
  count: () => handles.length,
  toggle(handle) {
    if (!handle) return false;
    const added = !handles.includes(handle);
    handles = added ? [...handles, handle] : handles.filter((h) => h !== handle);
    write(handles);
    changed(handle, added, added ? [] : [handle]);
    return added;
  },
  // Drops several handles in one write (the wishlist page prunes products that no longer exist).
  remove(list) {
    const drop = handles.filter((h) => list.includes(h));
    if (!drop.length) return;
    handles = handles.filter((h) => !drop.includes(h));
    write(handles);
    changed(null, false, drop);
  },
  // A product whose handle changed: saved under the new one, in the same place.
  rename(from, to) {
    if (!to || from === to || !handles.includes(from)) return;
    handles = handles.includes(to) ? handles.filter((h) => h !== from) : handles.map((h) => (h === from ? to : h));
    write(handles);
    changed(null, null, [from]);
  },
  renderCounts,
  renderButtons,
};

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-wishlist-toggle][data-handle]');
  if (!button) return;
  event.preventDefault();
  DE.wishlist.toggle(button.dataset.handle);
});

// Hearts that arrive later (lazy sections, theme editor).
['lazy-section:loaded', 'shopify:section:load'].forEach((type) =>
  document.addEventListener(type, (event) => renderButtons(event.target instanceof Element ? event.target : document))
);

// Other tabs.
window.addEventListener('storage', (event) => {
  if (event.key !== KEY && event.key !== null) return;
  handles = read();
  changed(null, null);
});

// A page restored from the back/forward cache may have missed changes made on the pages visited since.
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  const fresh = read();
  if (fresh.join('\n') === handles.join('\n')) return;
  handles = fresh;
  changed(null, null);
});

renderCounts();
renderButtons();
